<template>
    <div class="widget-component">
        <v-row>
            <v-col cols="12" md="6" id="bc-form">
                <!-- Full Value -->
                <div class="form-group mb-4" v-if="normalizedOptions.fullValue.visible">
                    <v-label v-if="selectOptions.length > 0">Plot</v-label>
                    <v-select v-if="selectOptions.length > 0"
                        v-model="selectedPlot"
                        :items="selectOptions"
                        label=""
                        item-text="text"
                        item-value="value"
                        outlined
                        @change="plotValueChanged"
                    ></v-select>

                    <v-label v-if="selectOptions.length == 0">{{ normalizedOptions.fullValue.label }}</v-label>
                    <v-text-field v-if="selectOptions.length == 0"
                        id="full_value"
                        v-model="fullValue"
                        @change="fullValueChanged"
                        label=""
                        outlined
                        hide-details="auto"
                    ></v-text-field>
                </div>

                <!-- Share Value -->
                <div class="form-group mb-5" v-if="this.normalizedOptions.shareValue.visible">
                    <v-label>{{ normalizedOptions.shareValue.label }}</v-label>
                    <v-text-field
                        id="share_value"
                        readonly
                        v-model="shareValue"
                        label=""
                        outlined
                        hide-details="auto"
                    ></v-text-field>
                </div>

                <!-- Share Percentage -->
                <div class="form-group" v-if="this.normalizedOptions.sharePercentage.visible">
                    <v-label>{{ normalizedOptions.sharePercentage.label }}</v-label>
                    <v-slider
                        v-model="sharePercentage"
                        :min="normalizedOptions.sharePercentage.min"
                        :max="normalizedOptions.sharePercentage.max"
                        :step="normalizedOptions.sharePercentage.step"
                        :thumb-label="true"
                        @change="sharePercentageChanged"
                    >
                        <template v-slot:thumb-label="{ value }">
                            {{ value }}%
                        </template>

                        <template v-slot:prepend>
                            {{ normalizedOptions.sharePercentage.min }}%
                        </template>

                        <template v-slot:append>
                            {{ normalizedOptions.sharePercentage.max }}%
                        </template>
                    </v-slider>
                </div>

                <!-- Deposit Percentage -->
                <div class="form-group" v-if="this.normalizedOptions.depositPercentage.visible">
                    <v-label>{{ normalizedOptions.depositPercentage.label }}</v-label>
                    <v-slider
                        v-model="depositPercentage"
                        :min="normalizedOptions.depositPercentage.min"
                        :max="normalizedOptions.depositPercentage.max"
                        :step="normalizedOptions.depositPercentage.step"
                        :thumb-label="true"
                        @change="depositPercentageChanged"
                    >
                        <template v-slot:thumb-label="{ value }">
                            {{ value }}%
                        </template>

                        <template v-slot:prepend>
                            {{ normalizedOptions.depositPercentage.min }}%
                        </template>

                        <template v-slot:append>
                            {{ normalizedOptions.depositPercentage.max }}%
                        </template>
                    </v-slider>
                </div>

                <!-- Deposit Value -->
                <div class="form-group" v-if="this.normalizedOptions.depositValue.visible">
                    <v-label>{{ normalizedOptions.depositValue.label }}</v-label>
                    <v-slider
                        v-model="depositValue"
                        :min="minDepositValue"
                        :max="normalizedOptions.depositValue.max"
                        :step="normalizedOptions.depositValue.step"
                        :thumb-label="true"
                        :thumb-size="50"
                        @change="depositValueChanged"
                    >
                        <template v-slot:thumb-label="{ value }">
                            {{ formatPrice(value) }}
                        </template>

                        <template v-slot:prepend>
                            {{ formatPrice(minDepositValue) }}
                        </template>

                        <template v-slot:append>
                            {{ formatPrice(normalizedOptions.depositValue.max) }}
                        </template>
                    </v-slider>
                </div>

                <!-- Mortgage Rate -->
                <div class="form-group" v-if="this.normalizedOptions.mortgageRate.visible">
                    <v-label>{{ normalizedOptions.mortgageRate.label }}</v-label>
                    <v-slider
                        v-model="interestRate"
                        :min="normalizedOptions.mortgageRate.min"
                        :max="normalizedOptions.mortgageRate.max"
                        :step="normalizedOptions.mortgageRate.step"
                        :thumb-label="true"
                        @change="interestRateChanged"
                    >
                        <template v-slot:thumb-label="{ value }">
                            {{ value }}%
                        </template>

                        <template v-slot:prepend>
                            {{ normalizedOptions.mortgageRate.min }}%
                        </template>

                        <template v-slot:append>
                            {{ normalizedOptions.mortgageRate.max }}%
                        </template>
                    </v-slider>
                </div>
            </v-col>

            <!-- Results -->
            <v-col cols="12" md="6" id="bc-results-container">
                <div id="bc-results">
                    <h2 class="mb-5">Results</h2>

                    <v-row>
                        <v-col cols="12">
                            <label for="monthly-result">
                                Total Monthly Cost
                                <small><i class="d-block">(including service charge)</i></small>
                            </label>

                            <v-text-field
                                id="monthly-result"
                                readonly
                                v-model="monthlyTotal"
                                label=""
                                outlined
                                hide-details="auto"
                            ></v-text-field>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <label for="mortgage-result">Mortgage</label>

                            <v-text-field
                                id="mortgage-result"
                                readonly
                                v-model="monthlyMortgage"
                                label=""
                                outlined
                                hide-details="auto"
                            ></v-text-field>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <label for="rent-result">Rent</label>

                            <v-text-field
                                id="rent-result"
                                readonly
                                v-model="monthlyRent"
                                label=""
                                outlined
                                hide-details="auto"
                            ></v-text-field>
                        </v-col>
                    </v-row>
                </div>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import {defineComponent, ref, computed, watch, onMounted} from '@vue/composition-api';

    export default defineComponent({
        name: 'BudgetCalculator',
        props: {
            defaultFullValue: {
                type: Number,
                default: 240000
            },

            defaultSharePercentage: {
                type: Number,
                default: 25
            },

            defaultDepositPercentage: {
                type: Number,
                default: 5
            },

            defaultDepositValue: {
                type: Number,
                default: 3000
            },

            interest: {
                type: Number,
                default: 3
            },

            term: {
                type: Number,
                default: 25
            },

            period: {
                type: Number,
                default: 12
            },

            unsoldPercentage: {
                type: Number,
                default: 2.75
            },

            currency: {
                type: String,
                default: 'GBP'
            },

            properties: {
                required: false
            },

            options: {
                type: Object
            },
        },
        setup(props, context) {
            const fullValue = ref(formatPrice(props.defaultFullValue));
            const sharePercentage = ref(props.defaultSharePercentage);
            const depositPercentage = ref(props.defaultDepositPercentage);
            const depositValue = ref(formatPrice(props.defaultDepositValue));
            const interestRate = ref(props.interest);
            const monthlyServiceCharge = ref(0);
            const selectOptions = ref([]);
            const selectedPlot = ref(0);

            /**
             * Parse full value for compute methods
             */
            const cleanFullValue = computed(() => {
                return parseValue(fullValue.value);
            });

            /**
             * Calculate Share Value from Percentage & Full Value
             */
            const shareValue = computed(() => {
                return formatPrice((sharePercentage.value / 100) * cleanFullValue.value);
            });

            /**
             * Parsed Share Value from Percentage & Full Value
             */
             const cleanShareValue = computed(() => {
                return parseValue((sharePercentage.value / 100) * cleanFullValue.value);
            });

            /**
             * Calculate Deposit from Share Value & Deposit Percentage
             */
            const minDepositValue = computed(() => {
                let result = (cleanShareValue.value / 100) * depositPercentage.value;

                return parseValue(Math.round(result/100)*100);
            });

            /**
             * Deduct the share value from the full value
             */
            const unsoldEquity = computed(() => {
                return parseValue(cleanFullValue.value - cleanShareValue.value);
            });

            /**
             * Deduct the deposit from the share
             */
            const shareMinusDeposit = computed(() => {
                return parseValue(cleanShareValue.value - parseValue(depositValue.value));
            });

            /**
             * Calculate the monthly rent value
             * [Percentage of unsold equity spread over period (e.g 12 months = monthly)]
             */
            const monthlyRent = computed(() => {
                let result = (unsoldEquity.value / 100) * props.unsoldPercentage / props.period
                return formatPrice(result);
            });

            /**
             * Calculate monthly mortgage value
             */
            const monthlyMortgage = computed(() => {
                let N = props.term * props.period;
                let I = (interestRate.value / 100) / props.period;
                let v = Math.pow((1 + I), N);
                let t = (I * v) / (v - 1);
                return formatPrice(shareMinusDeposit.value * t);
            });

            /**
             * Calculate monthly total with sum (rent + mortgage)
             */
            const monthlyTotal = computed(() => {
                return formatPrice(parseValue(monthlyRent.value) + parseValue(monthlyMortgage.value) + parseValue(monthlyServiceCharge.value));
            });

            /**
             * Default Options
             */
            const normalizedOptions = computed(() => {
                return Object.assign({
                    fullValue: {
                        label: 'Full Value',
                        usePlots: true,
                        visible: true
                    },
                    shareValue: {
                        label: 'Share Value',
                        visible: true
                    },
                    sharePercentage: {
                        label: 'Share Percentage',
                        step: 5,
                        min: 25,
                        max: 75,
                        visible: true
                    },
                    depositPercentage: {
                        label: 'Deposit Percentage',
                        step: 5,
                        min: 5,
                        max: 50,
                        visible: false
                    },
                    depositValue: {
                        label: 'Deposit Value',
                        step: 1000,
                        max: 50000,
                        visible: true
                    },
                    mortgageRate: {
                        label: 'Mortgage Rate',
                        step: 0.25,
                        min: 2,
                        max: 12,
                        visible: true
                    }
                }, props.options)
            });

            function plotValueChanged(e){
                fullValue.value = selectOptions.value[e].price;
                monthlyServiceCharge.value = selectOptions.value[e].monthlyServiceCharge;
                context.emit('fullValueChanged', fullValue.value);
            }

            function fullValueChanged(e){
                context.emit('fullValueChanged', fullValue.value);
            };

            function sharePercentageChanged(e){
                context.emit('sharePercentageChanged', sharePercentage.value);
            };

            function depositPercentageChanged(e){
                context.emit('depositPercentageChanged', depositPercentage.value);
            };

            function depositValueChanged(e){
                context.emit('depositValueChanged', depositValue.value);
            };

            function interestRateChanged(e){
                context.emit('interestRateChanged', interestRate.value);
            };

            /**
             * Currency Formatter
             */
            function formatPrice(value) {
                var formatter = new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                    currency: props.currency,
                });

                return formatter.format(value);
            };

            /**
             * Parse value to clean integer
             */
            function parseValue(value){
                return parseFloat(value.toString().replace(/[^0-9\.]/g,''));
            };

            /**
             * Create selectOptions from Properties
             */
            function setPlotOptions(plots){
                let key = 0;
                plots.forEach((i) => {
                    if(i.availabilityStatus.code !== 'coming-soon') {
                        selectOptions.value.push({
                            'id': i.id,
                            'text': 'Plot '+i.propertyNumber,
                            'value': key,
                            'price': i.fullMarketPrice,
                            'monthlyServiceCharge': i.monthlyServiceCharge,
                        })
                        if(key == 0){
                            fullValue.value = i.fullMarketPrice;
                            monthlyServiceCharge.value = i.monthlyServiceCharge;
                        }
                        key++;
                    }
                });
            };

            watch(() => props.properties, (newVal, oldVal) => {
                if(newVal.length > 0 && normalizedOptions.value.fullValue.usePlots){
                    setPlotOptions(newVal);
                }
            });

            onMounted(() => {
                setPlotOptions(props.properties);
            });

            return {
                normalizedOptions,
                formatPrice,

                fullValue,
                monthlyServiceCharge,
                selectedPlot,
                plotValueChanged,
                fullValueChanged,
                selectOptions,

                shareValue,

                sharePercentage,
                sharePercentageChanged,

                depositPercentage,
                depositPercentageChanged,

                depositValue,
                minDepositValue,
                depositValueChanged,

                interestRate,
                interestRateChanged,

                monthlyMortgage,
                monthlyRent,
                monthlyTotal
            }
        }
    });
</script>

<style lang="scss">
    #bc-form, #bc-results{
        .v-input{
            &--is-readonly{
                color: #fff !important;
                border-color: var(--v-primary-base) !important;
                background-color: var(--v-primary-base) !important;

                input{
                    color: inherit !important;
                }

                .v-input__slot{
                    border-color: var(--v-primary-base) !important;
                }
            }
        }

        .v-text-field__details{
            display: none;
        }

        h2, label{
            color: var(--v-primary-base);

            small{
                color: grey;
            }
        }

        label{
            font-weight: 500;
        }
    }

    #bc-results{
        padding: 34px 42px;
        border-radius: 4px;
        border: 1px solid var(--v-primary-base);

        .v-input{
            margin-top: 4px !important;
        }

        @media (max-width: 600px) {
            padding: 24px 32px;
        }
    }
</style>
